import { log as logger } from './log';
import { signal, computed, effect } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { QueryClient, QueryFunctionContext, QueryObserverOptions } from '@tanstack/query-core';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { LocateOrder } from '@thinkalpha/ui-types/contracts/locates/orders';
import type { LocateAccount } from '@thinkalpha/ui-types/contracts/locates/platforms';
import type { LocateProvider } from '@thinkalpha/ui-types/contracts/locates/providers';
import type { FlattenedLocateResultRow } from '@thinkalpha/ui-types/types/labModern';
import { isEqual, noop } from 'es-toolkit';
import type { TableStatus } from 'src/components/table-view/model';
import { RowDataSource } from 'src/features/InfiniteTimeView';
import type {
    DecorationRow,
    RowDataSourceConstructionOptions,
    RowDataSourceQueryKey,
    RowDataSourceQueryResult,
    RowDataSourceState,
} from 'src/features/InfiniteTimeView';
import { createTable } from 'src/lib/table';
import { FilterStage, OutputStage, SourceStage } from 'src/lib/tableStages';
import type { LocatesV2Filters } from 'src/models/LocatesV2WidgetModel/filters';
import { areAccountsUnchanged } from 'src/models/LocatesV2WidgetModel/updateAccountsGuard';
import type { TableModel } from 'src/models/TableModel';
import { getComparatorForSort, DEFAULT_LOCATES_V2_SORT } from 'src/queries/locatesV2/sorts';
import type { LocatesV2Sort } from 'src/queries/locatesV2/sorts';
import { noopFlattenedLocateResultDecoratorFunction } from 'src/routes/widgets/LocatesV2Widget/types';
import type { LocatesWorkerConnection } from 'src/services/LocatesWorkerService/connection';
import type { LocatesActivity, LocatesWorkerContract } from 'src/services/LocatesWorkerService/contract';
import type { WorkerClient } from 'src/services/workerHost/client';
import type { ReactBindings } from 'src/types/bindings';

const log = logger.getSubLogger({ name: 'LocatesRowDataSourceWorkerProvider' }, { component: 'lab' });

// Symbol tracking configuration
const SYMBOL_RETENTION_MS = 20_000; // Keep symbols for 20s after leaving viewport
const SYMBOL_CLEANUP_INTERVAL_MS = 10_000; // Check for expired symbols every 10s
const INITIAL_FETCH_ROW_LIMIT = 100_000;

type ViewportLocateRow = FlattenedLocateResultRow & {
    isEndOfDataDownward?: boolean;
    isEndOfDataUpward?: boolean;
    parity?: 'even' | 'odd';
};

function omitRowDataSourceMetadata(row: ViewportLocateRow): Record<string, unknown> {
    const data: Record<string, unknown> = { ...row };
    delete data.isEndOfDataDownward;
    delete data.isEndOfDataUpward;
    delete data.parity;
    return Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined));
}

function areViewportRowsEqual(currentRows: FlattenedLocateResultRow[], freshRows: FlattenedLocateResultRow[]): boolean {
    return (
        currentRows.length === freshRows.length &&
        currentRows.every((row, index) =>
            isEqual(omitRowDataSourceMetadata(row), omitRowDataSourceMetadata(freshRows[index])),
        )
    );
}

/**
 * State machine that decides which query options to return based on current state.
 * Routes to worker-backed queries instead of REST API.
 */
function createLocatesWorkerQueryOptions(
    worker: WorkerClient<LocatesWorkerContract>,
    currentQueryOptions: QueryObserverOptions<
        any,
        Error,
        RowDataSourceQueryResult<FlattenedLocateResultRow>,
        RowDataSourceQueryKey
    >,
    state: RowDataSourceState<FlattenedLocateResultRow, DecorationRow>,
    config: { bufferSize: number; overscanRows: number; sort: LocatesV2Sort },
): QueryObserverOptions<any, Error, RowDataSourceQueryResult<FlattenedLocateResultRow>, RowDataSourceQueryKey> {
    // If currently fetching, don't start another fetch
    if (state.isFetchingUp || state.isFetchingDown) {
        if (TRACE) {
            log.trace('Currently fetching, returning current options', {
                isFetchingUp: state.isFetchingUp,
                isFetchingDown: state.isFetchingDown,
            });
        }
        return currentQueryOptions;
    }

    // Don't fetch during viewport shift
    if (state.viewportShift !== 0) {
        return {
            enabled: false,
            queryKey: ['ROW_DATA_SOURCE', 'OTHER', 'viewport-shift'],
        };
    }

    // Initial fetch when no data and container is ready
    if (
        state.data.length === 0 &&
        !state.isFetchingDown &&
        !state.isFetchingUp &&
        state.clientHeight > 0 &&
        !state.hasReachedStart &&
        !state.hasReachedEnd
    ) {
        const estimatedViewportRows = Math.ceil(state.clientHeight / state.rowHeight);
        const expectedCount = estimatedViewportRows + config.overscanRows * 2;

        return {
            enabled: true,
            queryKey: ['ROW_DATA_SOURCE', 'INITIAL_FETCH', 'locates-v2-worker'],
            queryFn: async () => {
                if (TRACE) {
                    log.trace('Initial fetch from worker', { expectedCount });
                }

                const response = await worker.request('query', {
                    direction: 'initial',
                    limit: expectedCount,
                });

                if (TRACE) {
                    log.trace('Worker query completed', {
                        rowCount: response.rows.length,
                        expectedCount,
                        isEndOfData: response.isEndOfData,
                    });
                }

                return {
                    type: 'INITIAL_FETCH_SUCCESS' as const,
                    rows: response.rows,
                    isEndOfDataUpward: true,
                    isEndOfDataDownward: response.isEndOfData,
                };
            },
            gcTime: 0,
            staleTime: 0,
        };
    }

    // Manual fetch up (for live updates)
    if (state.manualFetchUpEnqueued && state.data.length > 0) {
        const dataRows = state.data.filter((row) => row.type === 'data') as FlattenedLocateResultRow[];
        const firstDataRow = dataRows[0];
        const expectedCount = config.bufferSize + config.overscanRows;

        if (!firstDataRow) {
            return {
                enabled: false,
                queryKey: ['ROW_DATA_SOURCE', 'OTHER', 'no-data-for-fetch-up'],
            };
        }

        return {
            enabled: true,
            queryKey: ['ROW_DATA_SOURCE', 'FETCH_UP', 'locates-v2-worker', firstDataRow.stableOrderId],
            queryFn: async () => {
                if (TRACE) {
                    log.trace('Fetch up from worker', {
                        expectedCount,
                        anchorStableOrderId: firstDataRow.stableOrderId,
                    });
                }

                const response = await worker.request('query', {
                    direction: 'up',
                    limit: expectedCount,
                    anchorStableOrderId: firstDataRow.stableOrderId,
                });

                return {
                    type: 'FETCH_UP_SUCCESS' as const,
                    rows: response.rows,
                    isEndOfData: response.isEndOfData,
                };
            },
            gcTime: 0,
            staleTime: 0,
        };
    }

    // Check if scrolling into buffer zones
    const range = state.visibleRange;
    let needsUpFetch = false;
    let needsDownFetch = false;

    const totalRows = state.topBuffer + state.data.length + state.bottomBuffer;
    for (let i = range.start; i <= range.end && i < totalRows; i++) {
        if (i < state.topBuffer && !state.hasReachedStart) {
            needsUpFetch = true;
        } else if (i >= state.topBuffer + state.data.length && !state.hasReachedEnd) {
            needsDownFetch = true;
        }
    }

    if (needsUpFetch && state.data.length > 0) {
        const dataRows = state.data.filter((row) => row.type === 'data') as FlattenedLocateResultRow[];
        const firstDataRow = dataRows[0];
        const expectedCount = config.bufferSize + config.overscanRows;

        if (!firstDataRow) {
            return {
                enabled: false,
                queryKey: ['ROW_DATA_SOURCE', 'OTHER', 'no-data-for-fetch-up'],
            };
        }

        return {
            enabled: true,
            queryKey: ['ROW_DATA_SOURCE', 'FETCH_UP', 'locates-v2-worker', firstDataRow.stableOrderId],
            queryFn: async () => {
                if (TRACE) {
                    log.trace('Fetch up from worker', {
                        expectedCount,
                        anchorStableOrderId: firstDataRow.stableOrderId,
                    });
                }

                const response = await worker.request('query', {
                    direction: 'up',
                    limit: expectedCount,
                    anchorStableOrderId: firstDataRow.stableOrderId,
                });

                return {
                    type: 'FETCH_UP_SUCCESS' as const,
                    rows: response.rows,
                    isEndOfData: response.isEndOfData,
                };
            },
            gcTime: 0,
            staleTime: 0,
        };
    }

    if (needsDownFetch && state.data.length > 0) {
        const dataRows = state.data.filter((row) => row.type === 'data') as FlattenedLocateResultRow[];
        const lastDataRow = dataRows[dataRows.length - 1];
        const expectedCount = config.bufferSize + config.overscanRows;

        if (!lastDataRow) {
            return {
                enabled: false,
                queryKey: ['ROW_DATA_SOURCE', 'OTHER', 'no-data-for-fetch-down'],
            };
        }

        return {
            enabled: true,
            queryKey: ['ROW_DATA_SOURCE', 'FETCH_DOWN', 'locates-v2-worker', lastDataRow.stableOrderId],
            queryFn: async () => {
                if (TRACE) {
                    log.trace('Fetch down from worker', {
                        expectedCount,
                        anchorStableOrderId: lastDataRow.stableOrderId,
                    });
                }

                const response = await worker.request('query', {
                    direction: 'down',
                    limit: expectedCount,
                    anchorStableOrderId: lastDataRow.stableOrderId,
                });

                return {
                    type: 'FETCH_DOWN_SUCCESS' as const,
                    rows: response.rows,
                    isEndOfData: response.isEndOfData,
                };
            },
            gcTime: 0,
            staleTime: 0,
        };
    }

    // No fetch needed
    return {
        enabled: false,
        queryKey: ['ROW_DATA_SOURCE', 'OTHER', 'idle'],
    };
}

/**
 * Creates a query options factory for the LocatesV2Widget RowDataSource backed by a worker.
 */
function createLocatesWorkerQueryOptionsFactory(
    worker: WorkerClient<LocatesWorkerContract>,
    config: {
        bufferSize: number;
        overscanRows: number;
        sort: LocatesV2Sort;
    },
): (
    stateSignal: ReadonlySignal<any>,
) => ReadonlySignal<
    QueryObserverOptions<any, Error, RowDataSourceQueryResult<FlattenedLocateResultRow>, RowDataSourceQueryKey>
> {
    return (stateSignal) => {
        // A replacement store must never hydrate a viewport from the previous access scope.
        const queryScope = crypto.randomUUID();
        let lastQueryOptions: QueryObserverOptions<
            any,
            Error,
            RowDataSourceQueryResult<FlattenedLocateResultRow>,
            RowDataSourceQueryKey
        > | null = null;

        const queryOptionsSignal = computed(() => {
            const state = stateSignal.value;
            const currentOptions = lastQueryOptions ?? {
                enabled: false,
                queryKey: ['ROW_DATA_SOURCE', 'OTHER', 'idle'] as RowDataSourceQueryKey,
            };

            const newOptions = createLocatesWorkerQueryOptions(worker, currentOptions, state, config);
            if (newOptions !== currentOptions && newOptions.queryKey) {
                newOptions.queryKey = [...newOptions.queryKey, queryScope];
            }

            trace: if (TRACE) {
                if (newOptions.enabled !== true) {
                    break trace;
                }
                log.trace('Query options computed', {
                    enabled: newOptions.enabled,
                    queryKey: newOptions.queryKey,
                    dataLength: state.data.length,
                    isFetchingUp: state.isFetchingUp,
                    isFetchingDown: state.isFetchingDown,
                    clientHeight: state.clientHeight,
                });
            }

            return newOptions;
        });

        effect(() => {
            lastQueryOptions = queryOptionsSignal.value;
        });

        return queryOptionsSignal;
    };
}

/**
 * Provides a RowDataSource configured for displaying locates results backed by a web worker.
 *
 * This provider reads viewports from the worker’s canonical in-memory order store. Socket updates are managed by the worker via LocatesWidgetUpdateProvider,
 * and the worker notifies the main thread to re-query the viewport via filterCacheInvalidated messages.
 */
export class LocatesRowDataSourceWorkerProvider {
    #rowDataSourceSignal: Signal<RowDataSource<FlattenedLocateResultRow, DecorationRow>>;
    #dataSource: RowDataSource<FlattenedLocateResultRow, DecorationRow>;
    #queryClient: QueryClient;
    #worker: WorkerClient<LocatesWorkerContract>;
    #initialFetchPromise: Promise<void>;
    #isInitializing = signal(true);
    #isLoadingOrders = signal(false);
    #rowCounts = signal({ filteredCount: 0, totalCount: 0 });
    readonly isLoading: ReadonlySignal<boolean> = computed(
        () =>
            this.#isInitializing.value ||
            this.#isLoadingOrders.value ||
            this.#rowDataSourceSignal.value.isFetchingUp.value ||
            this.#rowDataSourceSignal.value.isFetchingDown.value,
    );
    readonly rowCountLabel: ReadonlySignal<string> = computed(() => {
        const { filteredCount, totalCount } = this.#rowCounts.value;
        return `${filteredCount.toLocaleString()} of ${totalCount.toLocaleString()}`;
    });
    #disposableStack = new DisposableStack();
    #currentFilters: LocatesV2Filters;
    #isDisposed = false;
    #viewportRefreshRequested = false;
    #viewportRefreshPromise: Promise<void> | null = null;
    #socketConnectionStatus: Signal<TableStatus>;
    #accounts: LocateAccount[] | undefined;
    #currentSort: LocatesV2Sort = DEFAULT_LOCATES_V2_SORT;

    // Symbol tracking for market data subscription
    #symbolTracker: Map<string, { symbol: string; markedForRemovalAt: number | null }> = new Map();
    #visibleSymbols: Signal<string[]>;
    #symbolCleanupInterval: ReturnType<typeof setInterval> | null = null;
    #symbolTrackingEffectCleanup: (() => void) | null = null;

    // Market data subscription
    #marketDataTable: TableModel;
    #slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    #marketDataMap: Signal<Map<string, { lastPrice: number | undefined; priceChange5m: number | undefined }>>;
    #shouldSubscribeMarketData: ReadonlySignal<boolean>;
    #onLocatesActivity: ((activity: LocatesActivity) => void) | undefined;

    #instanceId: string;

    constructor(
        queryClient: QueryClient,
        initialFilters: LocatesV2Filters,
        slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        marketDataTableModel: TableModel,
        shouldSubscribeMarketData: ReadonlySignal<boolean>,
        connection: Pick<LocatesWorkerConnection, 'acquire'>,
        accounts?: LocateAccount[],
        instanceId?: string,
        onLocatesActivity?: (activity: LocatesActivity) => void,
    ) {
        this.#instanceId = instanceId ?? crypto.randomUUID().slice(0, 8);
        this.#queryClient = queryClient;
        this.#worker = this.#disposableStack.use(connection.acquire()).client;
        this.#currentFilters = initialFilters;
        this.#slugMapServiceProvider = slugMapServiceProvider;
        this.#marketDataTable = marketDataTableModel;
        this.#shouldSubscribeMarketData = shouldSubscribeMarketData;
        this.#accounts = accounts;
        this.#onLocatesActivity = onLocatesActivity;
        this.#socketConnectionStatus = signal<TableStatus>('uninitialized');
        this.#marketDataMap = signal(new Map());
        this.#disposableStack.use(
            this.#worker.events.on('ordersLoadingChanged', ({ loading }) => {
                this.#isLoadingOrders.value = loading;
            }),
        );
        this.#disposableStack.use(
            this.#worker.events.on('rowCountsChanged', (counts) => {
                this.#rowCounts.value = counts;
            }),
        );
        this.#disposableStack.use(
            this.#worker.events.on('filterCacheInvalidated', () => this.#handleFilterCacheInvalidated()),
        );
        this.#disposableStack.use(
            this.#worker.events.on('socketConnectionStatus', ({ status }) => {
                this.#socketConnectionStatus.value = status;
            }),
        );
        this.#disposableStack.use(
            this.#worker.events.on('locatesActivity', ({ activity }) => this.#onLocatesActivity?.(activity)),
        );
        this.#disposableStack.use(
            this.#worker.events.on('ordersReset', () => {
                this.#dataSource[Symbol.dispose]();
                this.#dataSource = this.#createRowDataSource(this.#queryClient, this.#currentSort);
                this.#rowDataSourceSignal.value = this.#dataSource;
                this.#setupSymbolTrackingEffect();
            }),
        );
        this.#disposableStack.defer(
            effect(() => {
                if (this.#worker.state.value === 'errored') this.#socketConnectionStatus.value = 'disconnected';
            }),
        );

        // Start initial fetch in background
        this.#initialFetchPromise = this.#initializeWorker();
        // Keep the rejection available to consumers without leaking an unhandled background rejection.
        void this.#initialFetchPromise.catch(noop);
        void this.#worker.ready.catch((error: unknown) => log.error('Locates worker failed to boot', { error }));

        this.#dataSource = this.#createRowDataSource(queryClient, this.#currentSort);
        this.#rowDataSourceSignal = signal(this.#dataSource);

        // Initialize symbol tracking
        this.#visibleSymbols = signal<string[]>([]);
        this.#setupSymbolTrackingEffect();

        // Start cleanup interval for expired symbols
        this.#symbolCleanupInterval = setInterval(() => {
            this.#cleanupExpiredSymbols();
        }, SYMBOL_CLEANUP_INTERVAL_MS);

        // Log symbol list changes for debugging
        this.#disposableStack.defer(
            effect(() => {
                const symbols = this.#visibleSymbols.value;
                if (TRACE) {
                    log.trace('Visible symbols list updated', {
                        symbols: JSON.stringify(symbols),
                        count: symbols.length,
                    });
                }
            }),
        );

        // Initialize market data subscription
        this.#initializeMarketDataSubscription();
    }

    async #initializeWorker(): Promise<void> {
        try {
            // Send initial filters to worker
            log.info('Sending initial filters to worker', { filters: this.#currentFilters });
            await this.#worker.request('updateFilters', {
                filters: this.#currentFilters,
            });

            log.info('Sending initial fetch request to worker');
            const response = await this.#worker.request('initialFetch', {
                limit: INITIAL_FETCH_ROW_LIMIT,
            });

            log.info('Worker initial fetch complete', { orderCount: response.orderCount });
        } catch (error) {
            log.error('Failed to initialize worker', { error });
            throw error;
        } finally {
            this.#isInitializing.value = false;
        }
    }

    /**
     * Sets up an effect to observe the dataSource state and update symbol tracking.
     * This needs to be called whenever the dataSource is recreated.
     */
    #setupSymbolTrackingEffect(): void {
        // Clean up previous effect if it exists
        if (this.#symbolTrackingEffectCleanup) {
            this.#symbolTrackingEffectCleanup();
            this.#symbolTrackingEffectCleanup = null;
        }

        // Create new effect to observe dataSource state
        this.#symbolTrackingEffectCleanup = effect(() => {
            this.#updateSymbolTracking();
        });
    }

    /**
     * Updates symbol tracking based on current viewport data.
     * - Adds new symbols to tracking
     * - Unmarks symbols that return to viewport
     * - Marks symbols for removal when they leave viewport
     */
    #updateSymbolTracking(): void {
        if (TRACE) {
            log.trace('Updating symbol tracking');
        }
        // Access the private state signal from RowDataSource
        const state = this.#dataSource['state'].value;
        const dataRows = state.data.filter((row) => row.type === 'data') as FlattenedLocateResultRow[];

        // Get current symbols in viewport
        const currentSymbols = new Set(dataRows.map((row) => row.symbol));
        const now = Date.now();

        // Batch tracking for logging
        const added: string[] = [];
        const unmarked: string[] = [];
        const markedForRemoval: string[] = [];

        // Track new symbols and unmark returning symbols
        for (const symbol of currentSymbols) {
            const existing = this.#symbolTracker.get(symbol);
            if (!existing) {
                // New symbol - add it
                this.#symbolTracker.set(symbol, { symbol, markedForRemovalAt: null });
                added.push(symbol);
            } else if (existing.markedForRemovalAt !== null) {
                // Symbol returned to viewport - unmark it
                existing.markedForRemovalAt = null;
                unmarked.push(symbol);
            }
        }

        // Mark symbols that left the viewport
        for (const [symbol, tracked] of this.#symbolTracker) {
            if (!currentSymbols.has(symbol) && tracked.markedForRemovalAt === null) {
                tracked.markedForRemovalAt = now;
                markedForRemoval.push(symbol);
            }
        }

        // Log batched changes if any occurred
        const hasChanges = added.length > 0 || unmarked.length > 0 || markedForRemoval.length > 0;
        if (hasChanges) {
            if (TRACE) {
                log.trace('Symbol tracking updated', {
                    viewportSymbols: Array.from(currentSymbols).sort(),
                    added: added.length > 0 ? added.sort() : undefined,
                    unmarked: unmarked.length > 0 ? unmarked.sort() : undefined,
                    markedForRemoval: markedForRemoval.length > 0 ? markedForRemoval.sort() : undefined,
                });
            }
            this.#updateVisibleSymbolsSignal();
        }
    }

    /**
     * Removes symbols that have been marked for removal longer than SYMBOL_RETENTION_MS.
     * Called periodically by the cleanup interval.
     */
    #cleanupExpiredSymbols(): void {
        const now = Date.now();
        const expired: string[] = [];

        for (const [symbol, tracked] of this.#symbolTracker) {
            if (tracked.markedForRemovalAt !== null && now - tracked.markedForRemovalAt >= SYMBOL_RETENTION_MS) {
                expired.push(symbol);
            }
        }

        if (expired.length > 0) {
            for (const symbol of expired) {
                this.#symbolTracker.delete(symbol);
            }
            if (TRACE) {
                log.trace('Symbols removed from tracking (expired)', { expired: expired.sort() });
            }
            this.#updateVisibleSymbolsSignal();
        }
    }

    /**
     * Updates the visibleSymbols signal with current tracked symbols.
     */
    #updateVisibleSymbolsSignal(): void {
        const symbols = Array.from(this.#symbolTracker.keys()).sort();
        this.#visibleSymbols.value = symbols;

        if (TRACE) {
            log.trace('Visible symbols list updated', {
                symbols: JSON.stringify(symbols),
                count: symbols.length,
            });
        }
    }

    /**
     * Initializes the market data subscription for price-related columns.
     * Sets up a TableModel that subscribes to LastPrice and PriceChange5m for visible symbols.
     */
    async #initializeMarketDataSubscription(): Promise<void> {
        try {
            log.info('Initializing market data subscription...');

            // Get SlugMapService from provider
            const slugMapService = await this.#slugMapServiceProvider();
            log.info('SlugMapService resolved', {
                slugMapKeys: Object.keys(slugMapService.slugMap.value).length,
            });

            // Get required slugs for our output columns
            const symbolSlug = computed(() => slugMapService.slugMap.value[Slugs.symbol]);
            const lastSlug = computed(() => slugMapService.slugMap.value[Slugs.last]);
            const changeSlug = computed(() => slugMapService.slugMap.value[Slugs.change]);
            const barCloseSlug = computed(() => slugMapService.slugMap.value[Slugs.barClose]);

            // Create query options that react to visibleSymbols and subscription eligibility changes
            const queryOptions = computed(() => {
                const symbols = this.#visibleSymbols.value;
                const shouldSubscribe = this.#shouldSubscribeMarketData.value;
                const symbolIndicator = symbolSlug.value;
                const lastIndicator = lastSlug.value;
                const changeIndicator = changeSlug.value;
                const barCloseIndicator = barCloseSlug.value;

                // Skip if subscription not eligible, no symbols, or indicators not loaded yet
                if (
                    !shouldSubscribe ||
                    symbols.length === 0 ||
                    !symbolIndicator ||
                    !lastIndicator ||
                    !changeIndicator ||
                    !barCloseIndicator
                ) {
                    log.info('Skipping market data table - not eligible or missing data', {
                        shouldSubscribe,
                        symbolCount: symbols.length,
                        hasSymbolIndicator: !!symbolIndicator,
                        hasLastIndicator: !!lastIndicator,
                        hasChangeIndicator: !!changeIndicator,
                        hasBarCloseIndicator: !!barCloseIndicator,
                    });
                    return {
                        queryKey: ['market-data-locates', 'empty'],
                        enabled: false,
                    };
                }

                log.info('Building market data query options', {
                    symbolCount: symbols.length,
                    symbols: symbols.slice(0, 200),
                });

                return {
                    queryKey: ['market-data-locates', symbols],
                    queryFn: async (_context: QueryFunctionContext, debugIdentifier?: string) => {
                        if (TRACE) {
                            log.trace('Building market data table query', {
                                symbolCount: symbols.length,
                                symbols: symbols.slice(0, 200), // First 200 symbols for logging
                            });
                        }

                        // Create source stage for all tickers
                        const sourceStage = SourceStage.allForKey('ticker');

                        // Add filter stage with symbol filter
                        const filterStage = FilterStage.fromInputStage(sourceStage, {
                            type: 'filter',
                            // Build filter formula: (Symbol == "AAPL") or (Symbol == "FIG") or ...
                            formula: symbols.map((sym) => `(symbol == "${sym}")`).join(' or '),
                            imports: [{ id: symbolIndicator.id, alias: 'symbol' }],
                        });

                        // Add output stage with our columns
                        const outputStage = OutputStage.fromInputStage(filterStage, {}, [
                            // symbol column
                            {
                                id: 'symbol',
                                type: 'indicator',
                                indicator: { id: symbolIndicator.id },
                            },
                            // lastPrice column using LastPrice()
                            {
                                id: 'lastPrice',
                                type: 'formula',
                                formula: 'LastPrice()',
                                imports: [{ id: lastIndicator.id, alias: 'LastPrice' }],
                            },
                            // priceChange5m column using CHANGE(BarClose(10s), [.5m])
                            {
                                id: 'priceChange5m',
                                type: 'formula',
                                formula: 'CHANGE(BarClose(10s), [.5m])',
                                imports: [
                                    { id: changeIndicator.id, alias: 'CHANGE' },
                                    { id: barCloseIndicator.id, alias: 'BarClose' },
                                ],
                            },
                        ]);

                        return createTable({ leafStage: outputStage.build() }, undefined, debugIdentifier);
                    },
                    enabled: true,
                    staleTime: 0,
                    gcTime: 0,
                };
            });

            // Initialize the table with our query options
            this.#marketDataTable.init({
                initBounds: { firstRow: 0, windowSize: 10000 },
                initThrottle: 1000,
                queryOptions,
                devtoolsName: 'LocatesV2/marketData',
            });

            this.#disposableStack.defer(
                effect(() => {
                    const updateData = this.#marketDataTable.updateSignal.value?.update;
                    if (!updateData) return;

                    const symbol = updateData['symbol'] as string | undefined;
                    const lastPrice = updateData['lastPrice'] as number | undefined;
                    const priceChange5m = updateData['priceChange5m'] as number | undefined;

                    if (TRACE) {
                        log.trace('Market data signal based update', {
                            update: { symbol, lastPrice, priceChange5m },
                        });
                    }

                    if (symbol) {
                        // Clone and update map to trigger reactivity
                        const newMap = new Map(this.#marketDataMap.peek());
                        newMap.set(symbol, { lastPrice, priceChange5m });
                        this.#marketDataMap.value = newMap;
                    }
                }),
            );

            log.info('Market data subscription initialized');
        } catch (error) {
            log.error('Failed to initialize market data subscription', { error });
        }
    }

    get rowDataSource(): ReadonlySignal<RowDataSource<FlattenedLocateResultRow, DecorationRow>> {
        return this.#rowDataSourceSignal;
    }

    get dataSource(): RowDataSource<FlattenedLocateResultRow, DecorationRow> {
        return this.#dataSource;
    }

    /**
     * Reconcile server-confirmed API responses in the worker's canonical store.
     * Socket and API payloads share LocateOrder.id, the legacy stable-result ID.
     */
    async upsertOrders(orders: LocateOrder[]): Promise<void> {
        await this.#initialFetchPromise;
        const response = await this.#worker.request('upsertOrders', {
            orders,
        });
        if (!response.success) {
            throw new Error('Failed to reconcile locate orders in worker store');
        }
    }

    get worker(): WorkerClient<LocatesWorkerContract> {
        return this.#worker;
    }

    get socketConnectionStatus(): ReadonlySignal<TableStatus> {
        return this.#socketConnectionStatus;
    }

    get marketDataMap(): ReadonlySignal<
        Map<string, { lastPrice: number | undefined; priceChange5m: number | undefined }>
    > {
        return this.#marketDataMap;
    }

    /**
     * Handle filter cache invalidation from worker (e.g., when account names are fetched or socket updates occur).
     * Re-queries the current viewport to reflect updated data.
     */
    #handleFilterCacheInvalidated(): void {
        if (this.#isDisposed) {
            return;
        }

        this.#viewportRefreshRequested = true;
        if (this.#viewportRefreshPromise) {
            return;
        }

        this.#viewportRefreshPromise = this.#drainViewportRefreshes().finally(() => {
            this.#viewportRefreshPromise = null;
            if (this.#viewportRefreshRequested && !this.#isDisposed) {
                this.#handleFilterCacheInvalidated();
            }
        });
    }

    async #drainViewportRefreshes(): Promise<void> {
        while (this.#viewportRefreshRequested && !this.#isDisposed) {
            this.#viewportRefreshRequested = false;
            await this.#refreshViewport();
        }
    }

    async #refreshViewport(): Promise<void> {
        try {
            // Get current RowDataSource state to determine anchor and viewport size
            const dataSource = this.#dataSource;
            const dataSourceState = dataSource['state'].value;
            const dataRows = dataSourceState.data.filter((row) => row.type === 'data') as FlattenedLocateResultRow[];
            const firstCachedRow = dataRows[0];
            const cachedStableOrderIds = dataRows.map(({ stableOrderId }) => stableOrderId);

            const firstVisibleStateIndex = Math.max(
                0,
                Math.floor(dataSourceState.scrollTop / dataSourceState.rowHeight) - dataSourceState.topBuffer,
            );
            const visibleCachedRow = dataSourceState.data.find(
                (row, index) => index >= firstVisibleStateIndex && row.type === 'data',
            ) as FlattenedLocateResultRow | undefined;
            const visibleCachedRowOffset = visibleCachedRow
                ? dataRows.findIndex(({ stableOrderId }) => stableOrderId === visibleCachedRow.stableOrderId)
                : 0;

            // Calculate viewport size
            const estimatedViewportRows = Math.ceil(dataSourceState.clientHeight / dataSourceState.rowHeight);
            const bufferConfig = {
                bufferSize: 5,
                overscanRows: 20,
            };
            const limit = Math.max(dataRows.length, estimatedViewportRows + bufferConfig.overscanRows * 2);

            // Determine if user is at the top
            const isAtTop = dataSourceState.hasReachedStart && dataSourceState.scrollTop <= 10;
            const anchorStableOrderId = isAtTop ? null : (firstCachedRow?.stableOrderId ?? null);

            if (TRACE) {
                log.trace('Re-querying viewport after filter cache invalidation', {
                    anchorStableOrderId,
                    isAtTop,
                    limit,
                });
            }

            const fallbackAnchorOffset = Math.max(0, visibleCachedRowOffset);
            const fallbackAnchorStableOrderIds = anchorStableOrderId
                ? dataRows
                      .slice(fallbackAnchorOffset)
                      .map(({ stableOrderId }) => stableOrderId)
                      .filter((stableOrderId) => stableOrderId !== anchorStableOrderId)
                : undefined;
            const queryViewport = (queryAnchorStableOrderId: string | null) =>
                this.#worker.request('query', {
                    direction: queryAnchorStableOrderId ? 'down' : 'initial',
                    limit,
                    anchorStableOrderId: queryAnchorStableOrderId ?? undefined,
                    fallbackAnchorOffset: queryAnchorStableOrderId ? fallbackAnchorOffset : undefined,
                    fallbackAnchorStableOrderIds: queryAnchorStableOrderId ? fallbackAnchorStableOrderIds : undefined,
                    includeAnchor: Boolean(queryAnchorStableOrderId),
                });

            const viewportSnapshotChanged = () => {
                const currentState = dataSource['state'].value;
                const currentStableOrderIds = currentState.data
                    .filter((row) => row.type === 'data')
                    .map(({ stableOrderId }) => stableOrderId);

                return (
                    this.#dataSource !== dataSource ||
                    currentState.scrollTop !== dataSourceState.scrollTop ||
                    currentState.clientHeight !== dataSourceState.clientHeight ||
                    currentState.rowHeight !== dataSourceState.rowHeight ||
                    currentState.topBuffer !== dataSourceState.topBuffer ||
                    currentState.bottomBuffer !== dataSourceState.bottomBuffer ||
                    currentState.hasReachedStart !== dataSourceState.hasReachedStart ||
                    currentState.hasReachedEnd !== dataSourceState.hasReachedEnd ||
                    currentState.isFetchingUp !== dataSourceState.isFetchingUp ||
                    currentState.isFetchingDown !== dataSourceState.isFetchingDown ||
                    currentState.viewportShift !== dataSourceState.viewportShift ||
                    currentState.visibleRange.start !== dataSourceState.visibleRange.start ||
                    currentState.visibleRange.end !== dataSourceState.visibleRange.end ||
                    currentStableOrderIds.length !== cachedStableOrderIds.length ||
                    currentStableOrderIds.some((stableOrderId, index) => stableOrderId !== cachedStableOrderIds[index])
                );
            };

            const shouldDiscardResponse = () => {
                const snapshotChanged = viewportSnapshotChanged();
                if (!this.#isDisposed && snapshotChanged) {
                    this.#viewportRefreshRequested = true;
                }

                return this.#isDisposed || this.#viewportRefreshRequested || snapshotChanged;
            };

            // Query fresh viewport data.
            let response = await queryViewport(anchorStableOrderId);
            if (shouldDiscardResponse()) {
                return;
            }

            let resolvedAnchorStableOrderId = anchorStableOrderId;
            if (anchorStableOrderId && response.rows.length === 0) {
                response = await queryViewport(null);
                if (shouldDiscardResponse()) {
                    return;
                }
                resolvedAnchorStableOrderId = null;
            } else if (
                anchorStableOrderId &&
                !response.rows.some(({ stableOrderId }) => stableOrderId === anchorStableOrderId)
            ) {
                resolvedAnchorStableOrderId =
                    fallbackAnchorStableOrderIds?.find((stableOrderId) =>
                        response.rows.some((row) => row.stableOrderId === stableOrderId),
                    ) ?? null;
            }

            const currentState = dataSource['state'].value;
            const currentDataRows = currentState.data.filter(
                (row) => row.type === 'data',
            ) as FlattenedLocateResultRow[];

            const isEndOfDataUpward = response.isStartOfData;
            const isEndOfDataDownward = response.isEndOfData;
            const viewportIsUnchanged =
                currentState.hasReachedStart === isEndOfDataUpward &&
                currentState.hasReachedEnd === isEndOfDataDownward &&
                areViewportRowsEqual(currentDataRows, response.rows);

            if (TRACE) {
                log.trace('Replacing viewport with fresh filtered data', {
                    rowCount: response.rows.length,
                    isEndOfDataUpward,
                    isEndOfDataDownward,
                });
            }

            if (!viewportIsUnchanged) {
                dataSource.replaceViewportRows(response.rows, isEndOfDataUpward, isEndOfDataDownward);
            }

            if (TRACE) {
                log.trace('filterCacheInvalidated handled', {
                    instanceId: this.#instanceId,
                    workerRowCount: response.rows.length,
                    isAtTop,
                    anchorStableOrderId: resolvedAnchorStableOrderId ?? 'none',
                    limit,
                    isEndOfDataUpward,
                    isEndOfDataDownward: response.isEndOfData,
                    clientHeight: dataSourceState.clientHeight,
                    viewportIsUnchanged,
                });
            }
        } catch (error) {
            log.error('Failed to handle filter cache invalidation', { error, instanceId: this.#instanceId });
        }
    }

    async updateFilters(filters: LocatesV2Filters): Promise<void> {
        // Update local filters
        this.#currentFilters = filters;

        // Notify worker of filter change
        try {
            await this.#worker.request('updateFilters', {
                filters,
            });

            log.info('Updated filters in worker', {
                instanceId: this.#instanceId,
                hasTextSearch: Boolean(filters.textSearch),
                filterCount: Object.keys(filters.columnFilters ?? {}).length,
                selectedAccountCount: filters.selectedAccountIds?.length ?? 0,
                onlyPending: filters.onlyPending,
                onlyResults: filters.onlyResults,
                onlyUserActivity: filters.onlyUserActivity,
                hasCurrentUserId: filters.currentUserId !== null,
            });
        } catch (error) {
            log.error('Failed to update filters in worker', { error, filters });
            throw error;
        }

        this.#dataSource[Symbol.dispose]();
        this.#dataSource = this.#createRowDataSource(this.#queryClient, this.#currentSort);
        this.#rowDataSourceSignal.value = this.#dataSource;
        this.#setupSymbolTrackingEffect();
        log.info('Filters updated and data source recreated', {
            instanceId: this.#instanceId,
            hasTextSearch: Boolean(filters.textSearch),
            filterCount: Object.keys(filters.columnFilters ?? {}).length,
        });
    }

    updateAccounts(accounts: LocateAccount[]): void {
        // Safety-net equality check: skip the expensive recreateDataSource when the
        // account list is deeply equal to the previous one. The primary protection
        // against redundant fires is the deep-equal-guarded signal in impl.ts; this
        // guard is defense-in-depth against any path that reaches updateAccounts
        // with unchanged data.
        const unchanged = areAccountsUnchanged(this.#accounts, accounts);

        this.#accounts = accounts;

        if (unchanged) {
            if (TRACE) {
                log.trace('updateAccounts: accounts deeply equal, skipping data source recreation');
            }
            return;
        }

        // Recreate data source with updated accounts to apply new sort comparator
        this.recreateDataSource(this.#currentSort);
    }

    updateProviders(providers: LocateProvider[]): void {
        // Send providers to worker for column filtering by provider name
        this.#worker
            .request('updateProviders', {
                providers,
            })
            .catch((error) => {
                log.error('Failed to send providers to worker', { error });
            });
    }

    recreateDataSource(sort: LocatesV2Sort = DEFAULT_LOCATES_V2_SORT, filters?: LocatesV2Filters): void {
        // Update filters if provided
        if (filters) {
            this.#currentFilters = filters;
            // Notify worker of filter change
            this.#worker
                .request('updateFilters', {
                    filters,
                })
                .catch((error) => {
                    log.error('Failed to update filters in worker', { error, filters });
                });
        }

        // Update current sort
        this.#currentSort = sort;

        // Notify worker of sort change
        this.#worker
            .request('updateSort', {
                sort,
            })
            .catch((error) => {
                log.error('Failed to update sort in worker', { error, sort });
            });

        // Recreate the data source
        this.#dataSource[Symbol.dispose]();
        this.#dataSource = this.#createRowDataSource(this.#queryClient, sort);
        this.#rowDataSourceSignal.value = this.#dataSource;
        this.#setupSymbolTrackingEffect();
    }

    #createRowDataSource(
        queryClient: QueryClient,
        sort: LocatesV2Sort,
    ): RowDataSource<FlattenedLocateResultRow, DecorationRow> {
        const bufferConfig = {
            bufferSize: 5,
            overscanRows: 20,
            sort,
        };

        const config: RowDataSourceConstructionOptions<FlattenedLocateResultRow, DecorationRow> = {
            rowHeight: 19,
            bufferSize: bufferConfig.bufferSize,
            overscanRows: bufferConfig.overscanRows,
            viewportDebounceDelay: 50,
            comparator: getComparatorForSort(sort, this.#accounts),
            decoratorFunction: noopFlattenedLocateResultDecoratorFunction,
            queryClient,
            queryOptionsFactory: createLocatesWorkerQueryOptionsFactory(this.#worker, bufferConfig),
        };

        return new RowDataSource<FlattenedLocateResultRow>(config);
    }

    [Symbol.dispose]() {
        this.#isDisposed = true;
        this.#disposableStack.dispose();
        this.#dataSource[Symbol.dispose]();

        // Clean up symbol tracking
        if (this.#symbolCleanupInterval) {
            clearInterval(this.#symbolCleanupInterval);
            this.#symbolCleanupInterval = null;
        }
        if (this.#symbolTrackingEffectCleanup) {
            this.#symbolTrackingEffectCleanup();
            this.#symbolTrackingEffectCleanup = null;
        }
    }
}
